// Booth-quote builder page (/booth-quote/).
// Handles product selection, URL-param prefill, validation, and Worker submit.
//
// Tier model: each product renders two chip groups in the layout, one per
// pricing tier (general / corporate). Liquid emits both; CSS hides the
// inactive one based on body[data-tier]. JS just toggles that attribute
// when the event-type changes — no per-chip price flipping. If the user
// flips tiers with items in the cart, we confirm and clear, because
// general and corporate options are different products with different
// labels and prices and trying to map them was the source of past bugs.

import { onReady } from "#public/utils/on-ready.js";

const SELECTION = new Map(); // slug -> { title, url, optionName, optionPrice, quantity }

let CURRENT_TIER = "general";

const getMinDate = () => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const d = String(t.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Tolerant match for an incoming event-type label. Both the booth-form
// widget and this select are now driven by the same eventTypes.json,
// so an exact match is the common case. The fuzzy branches stay as a
// safety net for old bookmarked URLs that still carry the legacy short
// labels (Birthday → Birthday, Corporate → Corporate Event, etc.).
const setSelectByLabel = (select, value) => {
  const incoming = value.trim().toLowerCase();
  if (!incoming) return;
  const options = [...select.options].filter((o) => o.value);

  let match = options.find(
    (o) =>
      o.value.toLowerCase() === incoming ||
      o.text.toLowerCase() === incoming,
  );

  if (!match) {
    match = options.find((o) => {
      const t = o.text.toLowerCase();
      return t.startsWith(incoming) || incoming.startsWith(t);
    });
  }

  if (!match) {
    const firstToken = incoming
      .split(/[\s/,&-]+/)
      .find((t) => t.length > 2);
    if (firstToken) {
      match = options.find((o) => o.text.toLowerCase().includes(firstToken));
    }
  }

  if (match) select.value = match.value;
};

const prefillFromUrl = (form) => {
  const params = new URLSearchParams(window.location.search);
  for (const input of form.querySelectorAll("[data-bq-prefill]")) {
    const key = input.getAttribute("data-bq-prefill");
    const value = params.get(key);
    if (!value) continue;
    if (input.tagName === "SELECT") {
      setSelectByLabel(input, value);
    } else {
      input.value = value;
    }
  }
};

const setMinEventDate = (form) => {
  const dateInput = form.querySelector("[name=event_date]");
  if (dateInput) dateInput.setAttribute("min", getMinDate());
};

const VAT_RATE = 0.20;
const METRES_PER_MILE = 1609.344;

const DELIVERY = {
  status: "idle",
  miles: 0,
  baseChargeNet: 0,
  isLondon: false,
  londonSupplement: 0,
  londonSupplementCorporate: 0,
  postcode: "",
};

// The London supplement depends on the active pricing tier, so the final
// charge is computed on demand rather than cached at fetch time — a tier
// flip after the postcode lookup must reprice delivery.
const currentLondonSupplement = () => {
  if (!DELIVERY.isLondon) return 0;
  return CURRENT_TIER === "corporate"
    ? DELIVERY.londonSupplementCorporate
    : DELIVERY.londonSupplement;
};

const deliveryChargeNet = () =>
  DELIVERY.baseChargeNet + currentLondonSupplement();

const formatGBP = (n) =>
  "£" + n.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const itemLineTotal = (item) =>
  (item.optionPrice || 0) * (item.quantity || 1);

// "Price on application" items (products with no priced options) carry
// poa: true in SELECTION. Their optionPrice is 0, so they never inflate
// the totals — but the UI must say "Price on application" rather than
// implying they're free.
const hasPoaItems = () => {
  for (const [, item] of SELECTION) {
    if (item.poa) return true;
  }
  return false;
};

const renderPoaNote = (container) => {
  const note = container.querySelector("[data-bq-poa-note]");
  if (note) note.hidden = !hasPoaItems();
};

// Applied coupon, exactly as the Worker's /coupon endpoint returned it:
// { code, type, value, label, min_spend, tier, products, categories, venue,
// venue_postcodes }. Only the rules
// come back, so the discount is recomputed here on every render and follows
// the cart as it changes. The Worker re-checks the code when the quote is
// sent. Discounts only come off products — delivery is always charged.
let COUPON = null;

const roundPence = (n) => Math.round(n * 100) / 100;

// Same matching as the Worker: no product/category lists = everything;
// otherwise a product counts if it matches either list.
const couponCovers = (slug, item) => {
  if (!COUPON.products && !COUPON.categories) return true;
  if (COUPON.products && COUPON.products.includes(slug)) return true;
  return Boolean(
    COUPON.categories &&
      (item.categories || []).some((c) => COUPON.categories.includes(c)),
  );
};

const couponEligibleNet = () => {
  let net = 0;
  for (const [slug, item] of SELECTION) {
    if (couponCovers(slug, item)) net += itemLineTotal(item);
  }
  return net;
};

const normalisePostcode = (postcode) =>
  (postcode || "").replace(/\s+/g, "").toUpperCase();

const venuePostcodeValue = () => {
  const input = document.querySelector("[data-bq-form] [name='venue_postcode']");
  return input ? input.value.trim() : "";
};

// null = no venue rule; "unknown" = rule but no postcode entered yet.
const couponVenueStatus = () => {
  if (!COUPON.venue_postcodes) return null;
  const venue = normalisePostcode(venuePostcodeValue());
  if (!venue) return "unknown";
  return COUPON.venue_postcodes.map(normalisePostcode).includes(venue)
    ? "match"
    : "mismatch";
};

const couponVenueName = () => COUPON.venue || "a specific venue";

// Why the applied code isn't taking effect right now, or null. Mirrors the
// Worker's checks (bar expiry, which it already passed on apply).
const couponIssue = (productsNet) => {
  if (couponVenueStatus() === "mismatch") {
    return `This code is only valid for events at ${couponVenueName()}.`;
  }
  if (COUPON.tier && COUPON.tier !== CURRENT_TIER) {
    return COUPON.tier === "corporate"
      ? "This code is for corporate events only."
      : "This code isn't valid for corporate events.";
  }
  if (COUPON.min_spend > 0 && productsNet < COUPON.min_spend) {
    return `This code needs at least ${formatGBP(COUPON.min_spend)} of products (before VAT).`;
  }
  if ((COUPON.products || COUPON.categories) && couponEligibleNet() === 0) {
    return "This code doesn't cover anything in your quote.";
  }
  return null;
};

const couponDiscountNet = (productsNet) => {
  if (!COUPON || couponIssue(productsNet)) return 0;
  const eligible = couponEligibleNet();
  if (COUPON.type === "percent") {
    return roundPence((eligible * Math.min(COUPON.value, 100)) / 100);
  }
  if (COUPON.type === "fixed") return Math.min(COUPON.value, eligible);
  return 0;
};

const computeTotals = () => {
  let productsNet = 0;
  for (const [, item] of SELECTION) productsNet += itemLineTotal(item);
  const discountNet = couponDiscountNet(productsNet);
  const deliveryNet = DELIVERY.status === "ok" ? deliveryChargeNet() : 0;
  const taxableNet = productsNet - discountNet + deliveryNet;
  const vat = taxableNet * VAT_RATE;
  return {
    productsNet,
    discountNet,
    deliveryNet,
    vat,
    gross: taxableNet + vat,
  };
};

const renderDiscountRow = (totalsEl, discountNet) => {
  const row = totalsEl.querySelector("[data-bq-discount-row]");
  if (!row) return;
  row.hidden = !(discountNet > 0);
  const labelEl = row.querySelector("[data-bq-discount-label]");
  const amountEl = row.querySelector("[data-bq-discount-amount]");
  if (labelEl && COUPON) labelEl.textContent = `Discount (${COUPON.code})`;
  if (amountEl) amountEl.textContent = `−${formatGBP(discountNet)}`;
};

const setCouponMsg = (couponEl, message, kind) => {
  const msg = couponEl.querySelector("[data-bq-coupon-msg]");
  if (!msg) return;
  msg.textContent = message || "";
  msg.hidden = !message;
  msg.classList.remove("is-ok", "is-error");
  if (kind) msg.classList.add(`is-${kind}`);
};

// Coupon box sits under the totals, so it only shows once there's
// something in the cart to discount.
const renderCoupon = () => {
  const couponEl = document.querySelector("[data-bq-coupon]");
  if (!couponEl) return;
  couponEl.hidden = SELECTION.size === 0;
  const input = couponEl.querySelector("[data-bq-coupon-input]");
  const btn = couponEl.querySelector("[data-bq-coupon-apply]");
  if (input) input.disabled = Boolean(COUPON);
  if (btn) btn.textContent = COUPON ? "Remove" : "Apply";
  if (!COUPON) return;
  const issue = couponIssue(computeTotals().productsNet);
  setCouponMsg(couponEl, issue || couponAppliedText(), issue ? "error" : "ok");
};

// Venue-limited codes say so until the venue postcode confirms it.
const couponAppliedText = () => {
  const text = `${COUPON.code} applied: ${COUPON.label}`;
  return couponVenueStatus() === "unknown"
    ? `${text}. Only valid for events at ${couponVenueName()}.`
    : text;
};

const renderTotals = (container) => {
  renderPoaNote(container);
  const totalsEl = container.querySelector("[data-bq-selection-totals]");
  if (!totalsEl) return;
  const t = computeTotals();
  if (t.productsNet <= 0) {
    totalsEl.hidden = true;
    return;
  }
  totalsEl.hidden = false;
  const setText = (selector, value) => {
    const el = totalsEl.querySelector(selector);
    if (el) el.textContent = formatGBP(value);
  };
  setText("[data-bq-total-net]", t.productsNet);
  setText("[data-bq-total-vat]", t.vat);
  setText("[data-bq-total-gross]", t.gross);
  renderDiscountRow(totalsEl, t.discountNet);

  const row = totalsEl.querySelector("[data-bq-delivery-row]");
  const labelEl = totalsEl.querySelector("[data-bq-delivery-label]");
  const amountEl = totalsEl.querySelector("[data-bq-delivery-amount]");
  if (!row || !labelEl || !amountEl) return;
  row.hidden = false;
  if (DELIVERY.status === "ok") {
    labelEl.textContent = `Delivery (${DELIVERY.miles.toFixed(1)} mi, net)`;
    amountEl.textContent = formatGBP(deliveryChargeNet());
  } else if (DELIVERY.status === "loading") {
    labelEl.textContent = "Delivery";
    amountEl.textContent = "Calculating…";
  } else {
    labelEl.textContent = "Delivery (TBC)";
    amountEl.textContent = "TBC";
  }
};

// Inline confirmation shown directly under the Venue Postcode field, so the
// customer sees the price react where they're looking — not only in the
// totals sidebar (which sits at the bottom of the page on mobile).
const renderDeliveryInline = () => {
  const el = document.querySelector("[data-bq-delivery-inline]");
  if (!el) return;
  el.classList.remove("is-loading", "is-ok", "is-error");
  if (DELIVERY.status === "loading") {
    el.hidden = false;
    el.classList.add("is-loading");
    el.textContent = "Calculating delivery price…";
  } else if (DELIVERY.status === "ok") {
    el.hidden = false;
    el.classList.add("is-ok");
    el.textContent = `Delivery added: ${formatGBP(deliveryChargeNet())} (${DELIVERY.miles.toFixed(1)} mi). Your quote total now includes it.`;
  } else if (DELIVERY.status === "error") {
    el.hidden = false;
    el.classList.add("is-error");
    el.textContent =
      "We couldn't price that postcode automatically — we'll confirm delivery by email.";
  } else {
    el.hidden = true;
    el.textContent = "";
  }
};

// Brief background flash on the totals block so a delivery update is
// noticeable even on desktop where the sidebar is always on screen.
const flashTotals = () => {
  const totalsEl = document.querySelector("[data-bq-selection-totals]");
  if (!totalsEl) return;
  totalsEl.classList.remove("is-flash");
  // Force a reflow so re-adding the class restarts the animation.
  void totalsEl.offsetWidth;
  totalsEl.classList.add("is-flash");
};

const updateFloatingCart = () => {
  const pill = document.querySelector("[data-bq-floating-cart]");
  if (!pill) return;
  if (SELECTION.size === 0) {
    pill.hidden = true;
    return;
  }
  pill.hidden = false;
  const totals = computeTotals();
  const countEl = pill.querySelector("[data-bq-floating-cart-count]");
  const priceEl = pill.querySelector("[data-bq-floating-cart-price]");
  if (countEl) countEl.textContent = String(SELECTION.size);
  if (priceEl) {
    // With only POA items in the cart the gross is 0 — "£0.00" would
    // read as "free", so show "POA" instead.
    priceEl.textContent =
      totals.gross === 0 && hasPoaItems() ? "POA" : formatGBP(totals.gross);
  }
};

const renderSelection = () => {
  renderDeliveryInline();
  const container = document.querySelector("[data-bq-selection]");
  if (!container) return;
  const items = container.querySelector("[data-bq-selection-items]");
  const empty = container.querySelector("[data-bq-selection-empty]");
  const continueBtn = container.querySelector("[data-bq-continue]");
  const saveQuoteEl = container.querySelector("[data-bq-save-quote]");

  items.innerHTML = "";
  if (continueBtn) {
    continueBtn.hidden = SELECTION.size === 0 || !isPickerStepActive();
  }
  if (saveQuoteEl) {
    const wasHidden = saveQuoteEl.hidden;
    const shouldHide = SELECTION.size === 0 || !isPickerStepActive();
    saveQuoteEl.hidden = shouldHide;
    // First time the save-quote sidebar becomes visible, mount its
    // Turnstile widget. Rendering it eagerly on page load is what was
    // producing the bulk of the third-party fingerprint console noise.
    if (wasHidden && !shouldHide) {
      const sitekey = saveQuoteEl.getAttribute("data-bq-turnstile-key") || "";
      renderTurnstile("bq-turnstile-save-quote", "save-quote", sitekey);
    }
  }
  renderCoupon();
  if (SELECTION.size === 0) {
    if (empty) empty.hidden = false;
    renderTotals(container);
    updateFloatingCart();
    return;
  }
  if (empty) empty.hidden = true;
  for (const [slug, productData] of SELECTION) {
    const qty = productData.quantity || 1;
    const lineTotal = itemLineTotal(productData);

    const li = document.createElement("li");
    li.className = "bq-selection__item";
    li.dataset.slug = slug;

    const top = document.createElement("div");
    top.className = "bq-selection__item-top";
    const title = document.createElement("span");
    let label = productData.title;
    if (productData.optionName) label += " — " + productData.optionName;
    if (productData.optionPrice) {
      label += " · " + formatGBP(productData.optionPrice);
    }
    title.textContent = label;
    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "bq-selection__remove";
    removeBtn.setAttribute("data-bq-remove", "");
    removeBtn.textContent = "Remove";
    top.appendChild(title);
    top.appendChild(removeBtn);

    const qtyRow = document.createElement("div");
    qtyRow.className = "bq-selection__qty";
    const dec = document.createElement("button");
    dec.type = "button";
    dec.className = "bq-selection__qty-btn";
    dec.setAttribute("data-bq-qty-dec", "");
    dec.textContent = "−";
    dec.setAttribute("aria-label", "Decrease quantity");
    if (qty <= 1) dec.disabled = true;
    const qtyValue = document.createElement("span");
    qtyValue.className = "bq-selection__qty-value";
    qtyValue.setAttribute("aria-live", "polite");
    qtyValue.textContent = String(qty);
    const inc = document.createElement("button");
    inc.type = "button";
    inc.className = "bq-selection__qty-btn";
    inc.setAttribute("data-bq-qty-inc", "");
    inc.textContent = "+";
    inc.setAttribute("aria-label", "Increase quantity");
    const lineTotalEl = document.createElement("span");
    lineTotalEl.className = "bq-selection__line-total";
    lineTotalEl.textContent = productData.poa
      ? "Price on application"
      : formatGBP(lineTotal);
    qtyRow.appendChild(dec);
    qtyRow.appendChild(qtyValue);
    qtyRow.appendChild(inc);
    qtyRow.appendChild(lineTotalEl);

    li.appendChild(top);
    li.appendChild(qtyRow);
    items.appendChild(li);
  }
  renderTotals(container);
  updateFloatingCart();
};

const readPricingTierFromForm = () => {
  const select = document.querySelector("[data-bq-form] [name='event_type']");
  if (!select) return "general";
  const opt = select.selectedOptions && select.selectedOptions[0];
  if (!opt) return "general";
  const tier = opt.getAttribute("data-pricing-tier");
  return tier === "corporate" ? "corporate" : "general";
};

const setBodyTier = (tier) => {
  document.body.setAttribute("data-tier", tier);
};

// Most recent value of the Event Type select. Used to revert the select
// if the user cancels the tier-change confirmation prompt.
let PREVIOUS_EVENT_TYPE_VALUE = "";

const TIER_CHANGE_MESSAGE =
  "Switching event type changes the pricing tier — your selected items will be cleared. Continue?";

// Sync everything that depends on the active tier:
//   - body[data-tier] (drives chip visibility via CSS)
//   - SELECTION (cleared on actual tier flip with items in cart)
//   - chip aria-pressed state across both tier groups
const refreshPricingTier = ({ skipConfirm = false } = {}) => {
  const next = readPricingTierFromForm();
  const select = document.querySelector("[data-bq-form] [name='event_type']");

  const tierChanging = next !== CURRENT_TIER;

  if (
    !skipConfirm &&
    tierChanging &&
    SELECTION.size > 0 &&
    typeof window !== "undefined" &&
    typeof window.confirm === "function"
  ) {
    const ok = window.confirm(TIER_CHANGE_MESSAGE);
    if (!ok) {
      if (select && PREVIOUS_EVENT_TYPE_VALUE != null) {
        select.value = PREVIOUS_EVENT_TYPE_VALUE;
      }
      return;
    }
  }

  if (select) PREVIOUS_EVENT_TYPE_VALUE = select.value;

  if (tierChanging) {
    CURRENT_TIER = next;
    setBodyTier(next);
    // Clear cart on a real tier flip — items in one tier don't carry
    // over to the other (different prices, sometimes different labels).
    if (SELECTION.size > 0) {
      const slugs = [...SELECTION.keys()];
      SELECTION.clear();
      for (const slug of slugs) updateProductCard(slug);
      renderSelection();
      reapplyRestrictions();
    } else {
      // Even with an empty cart the delivery line must re-render: the
      // London supplement differs between tiers.
      renderSelection();
    }
  } else {
    setBodyTier(next);
  }
};

const updateProductCard = (slug) => {
  const card = document.querySelector(
    `[data-bq-product][data-product-slug="${slug}"]`,
  );
  if (!card) return;
  const selected = SELECTION.get(slug);
  card.classList.toggle("is-selected", !!selected);
  const plainBtn = card.querySelector(".bq-product__add");
  if (plainBtn) {
    plainBtn.textContent = selected ? "Added · Remove" : "Add to quote";
  }
  // Chip pressed-state: a chip is "selected" if its option name and price
  // match the current SELECTION entry. Both tiers' chips get this pass;
  // only the visible one will actually show the highlight.
  for (const chip of card.querySelectorAll(".bq-chip")) {
    const chipName = chip.getAttribute("data-option-name") || "";
    const chipPrice = Number.parseFloat(
      chip.getAttribute("data-option-price") || "0",
    );
    const isThisChip =
      !!selected &&
      selected.optionName === chipName &&
      selected.optionPrice === chipPrice;
    chip.classList.toggle("is-selected", isThisChip);
    chip.setAttribute("aria-pressed", isThisChip ? "true" : "false");
  }
};

const toggleProduct = (card, button) => {
  const slug = card.dataset.productSlug;
  if (!slug) return;
  if (!isProductAddable(card)) return;

  const optionName = button
    ? button.getAttribute("data-option-name") || ""
    : "";
  const optionPrice = button
    ? Number.parseFloat(button.getAttribute("data-option-price") || "0") || 0
    : 0;
  const poa = !!button && button.hasAttribute("data-bq-poa");

  const existing = SELECTION.get(slug);
  if (
    existing &&
    existing.optionName === optionName &&
    existing.optionPrice === optionPrice
  ) {
    SELECTION.delete(slug);
  } else {
    const carryQty =
      existing && Number.isFinite(existing.quantity) && existing.quantity > 0
        ? existing.quantity
        : 1;
    SELECTION.set(slug, {
      title: card.dataset.productTitle,
      url: card.dataset.productUrl,
      categories: (card.dataset.productCategories || "")
        .split(",")
        .filter(Boolean),
      optionName,
      optionPrice,
      quantity: carryQty,
      poa,
    });
  }
  updateProductCard(slug);
  renderSelection();
  reapplyRestrictions();
};

const removeFromSelection = (slug) => {
  if (!slug || !SELECTION.has(slug)) return;
  SELECTION.delete(slug);
  updateProductCard(slug);
  renderSelection();
  reapplyRestrictions();
};

const requiredSlugsFor = (productEl) => {
  const raw = productEl.getAttribute("data-bq-requires-one-of") || "";
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
};

const isProductAddable = (productEl) => {
  const required = requiredSlugsFor(productEl);
  if (required.length === 0) return true;
  for (const slug of required) {
    if (SELECTION.has(slug)) return true;
  }
  return false;
};

const showToast = (message) => {
  const toast = document.querySelector("[data-bq-toast]");
  if (!toast) return;
  toast.textContent = message;
  toast.hidden = false;
  toast.classList.add("is-visible");
  if (toast._hideTimer) clearTimeout(toast._hideTimer);
  toast._hideTimer = setTimeout(() => {
    toast.classList.remove("is-visible");
    setTimeout(() => {
      toast.hidden = true;
    }, 350);
  }, 3000);
};

const reapplyRestrictions = () => {
  const productEls = document.querySelectorAll("[data-bq-product]");
  const removed = [];
  for (const productEl of productEls) {
    const required = requiredSlugsFor(productEl);
    if (required.length === 0) continue;
    const slug = productEl.getAttribute("data-product-slug");
    const addable = isProductAddable(productEl);

    for (const chip of productEl.querySelectorAll(".bq-chip")) {
      chip.disabled = !addable;
    }
    const plainBtn = productEl.querySelector(".bq-product__add");
    if (plainBtn) plainBtn.disabled = !addable;

    const msg = productEl.querySelector("[data-bq-restriction-msg]");
    if (msg) {
      msg.hidden = false;
      msg.classList.toggle("is-met", addable);
      msg.classList.toggle("is-not-met", !addable);
      msg.textContent = addable
        ? "Compatible with your photo booth"
        : "Not compatible with your photo booth";
    }

    if (!addable && slug && SELECTION.has(slug)) {
      SELECTION.delete(slug);
      const title = productEl.getAttribute("data-product-title") || "Item";
      removed.push(title);
      updateProductCard(slug);
    }
  }
  if (removed.length > 0) {
    const list = removed.join(", ");
    const verb = removed.length === 1 ? "is" : "are";
    showToast(
      list +
        " " +
        verb +
        " no longer compatible with your booth — removed from your quote.",
    );
    renderSelection();
  }
};

const adjustQuantity = (slug, delta) => {
  const item = SELECTION.get(slug);
  if (!item) return;
  const next = (item.quantity || 1) + delta;
  if (next < 1) return;
  item.quantity = next;
  renderSelection();
};

const setError = (form, message) => {
  const err = form.querySelector("[data-bq-form-error]");
  if (!err) return;
  err.textContent = message || "";
  err.hidden = !message;
};

const clearFieldErrors = (form) => {
  for (const f of form.querySelectorAll(".is-invalid")) {
    f.classList.remove("is-invalid");
  }
};

const markInvalid = (input) => {
  if (input && input.parentElement)
    input.parentElement.classList.add("is-invalid");
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateForm = (form) => {
  if (SELECTION.size === 0) {
    return "Please add at least one product to your quote.";
  }
  for (const field of form.querySelectorAll("[required]")) {
    if (!field.value.trim()) {
      markInvalid(field);
      field.focus();
      return "Please fill in all required fields.";
    }
  }
  const email = form.querySelector("[name=email]");
  if (email && !EMAIL_RE.test(email.value.trim())) {
    markInvalid(email);
    email.focus();
    return "Please enter a valid email address.";
  }
  const dateInput = form.querySelector("[name=event_date]");
  if (dateInput && dateInput.value < getMinDate()) {
    markInvalid(dateInput);
    dateInput.focus();
    return "Please pick a future event date.";
  }
  return null;
};

const fieldValue = (form, name) => {
  const el = form.elements[name];
  return el && typeof el.value === "string" ? el.value.trim() : "";
};

const buildProducts = () => {
  const products = [];
  for (const [slug, p] of SELECTION) {
    const qty = p.quantity || 1;
    // POA items send null price fields — the Worker's email renderer
    // omits the price for non-numbers, so nothing reads as "£0.00".
    const unit = p.poa ? null : p.optionPrice || 0;
    products.push({
      slug,
      title: p.title,
      url: p.url,
      categories: p.categories || [],
      option_name: p.optionName || null,
      option_price_net: unit,
      quantity: qty,
      line_total_net: unit === null ? null : unit * qty,
    });
  }
  return products;
};

const buildTotalsPayload = () => {
  const totals = computeTotals();
  return {
    subtotal_net: Number(totals.productsNet.toFixed(2)),
    discount_net: Number(totals.discountNet.toFixed(2)),
    coupon_code: totals.discountNet > 0 ? COUPON.code : null,
    delivery_charge_net:
      DELIVERY.status === "ok" ? Number(deliveryChargeNet().toFixed(2)) : null,
    delivery_miles:
      DELIVERY.status === "ok" ? Number(DELIVERY.miles.toFixed(2)) : null,
    vat_amount: Number(totals.vat.toFixed(2)),
    total_gross: Number(totals.gross.toFixed(2)),
  };
};

// Sent even for perk codes (no price change) so the team sees them. The
// tier lets the Worker re-check tier-limited codes.
const couponPayload = () =>
  COUPON ? { code: COUPON.code, tier: CURRENT_TIER } : null;

const buildPayload = (form) => ({
  turnstile_token: getTurnstileToken(form),
  website: fieldValue(form, "website"),
  customer: {
    first_name: fieldValue(form, "first_name"),
    last_name: fieldValue(form, "last_name"),
    company_name: fieldValue(form, "company_name"),
    email: fieldValue(form, "email"),
    mobile: fieldValue(form, "mobile"),
    street_address: fieldValue(form, "street_address"),
    city: fieldValue(form, "city"),
    postcode: fieldValue(form, "postcode"),
  },
  event: {
    name: fieldValue(form, "event_name"),
    type: fieldValue(form, "event_type"),
    date: fieldValue(form, "event_date"),
    start_time: fieldValue(form, "start_time"),
    end_time: fieldValue(form, "end_time"),
  },
  venue: {
    name: fieldValue(form, "venue_name"),
    address: fieldValue(form, "venue_address"),
    postcode: fieldValue(form, "venue_postcode"),
  },
  extras: {
    how_heard: fieldValue(form, "how_heard"),
    additional_details: fieldValue(form, "additional_details"),
  },
  products: buildProducts(),
  totals: buildTotalsPayload(),
  coupon: couponPayload(),
});

const getTurnstileToken = (form) => {
  const ts = form.querySelector("[name='cf-turnstile-response']");
  return ts && ts.value ? ts.value : "";
};

// Map of placeholder element id → Turnstile widget id (returned by
// turnstile.render). Used so we can reset / avoid re-render when the
// container is shown again.
const TURNSTILE_WIDGETS = new Map();

// Wait for window.turnstile to load (the api.js script is async defer).
// Resolves immediately if already loaded; polls otherwise.
const whenTurnstileReady = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.turnstile) return resolve(window.turnstile);
    const start = Date.now();
    const poll = () => {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - start > 10000) return resolve(null);
      setTimeout(poll, 100);
    };
    poll();
  });

const renderTurnstile = async (placeholderId, action, sitekey) => {
  const placeholder = document.getElementById(placeholderId);
  if (!placeholder || !sitekey) return;
  if (TURNSTILE_WIDGETS.has(placeholderId)) return;
  const turnstile = await whenTurnstileReady();
  if (!turnstile) return;
  try {
    const id = turnstile.render(`#${placeholderId}`, {
      sitekey,
      action,
      theme: "light",
    });
    if (id) TURNSTILE_WIDGETS.set(placeholderId, id);
  } catch (_e) {
    // No-op: render can throw if already mounted; we treat as render-once.
  }
};

const resetTurnstileWidget = (placeholderId) => {
  if (typeof window === "undefined" || !window.turnstile) return;
  const widgetId = TURNSTILE_WIDGETS.get(placeholderId);
  if (!widgetId) return;
  try {
    window.turnstile.reset(widgetId);
  } catch (_e) {
    // No-op
  }
};

const resetTurnstile = () => resetTurnstileWidget("bq-turnstile-lead");

const setSubmitting = (form, isSubmitting) => {
  const btn = form.querySelector("[data-bq-submit]");
  if (!btn) return;
  if (!btn.dataset.label) btn.dataset.label = btn.textContent.trim();
  btn.disabled = isSubmitting;
  btn.textContent = isSubmitting ? "Sending…" : btn.dataset.label;
};

const post = async (workerUrl, path, payload) => {
  try {
    const response = await fetch(`${workerUrl.replace(/\/$/, "")}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    return { status: response.status, data };
  } catch (_err) {
    return { status: 0, data: {} };
  }
};

// Booked → on to sign their contract on the customer portal (or, when the CRM
// sends them no address, the page that says to look for our email). A plain
// request (a booking the CRM passed to the team) → the usual thanks page.
const nextPage = (data) => {
  if (data.next) return data.next;
  return data.booked ? "/booth-quote-booked/" : "/booth-quote-thanks/";
};

const SEND_FAILED =
  "Sorry, something went wrong sending your request. Please try again or call us on 03331 126993.";

const submitForm = async (form) => {
  clearFieldErrors(form);
  const err = validateForm(form);
  if (err) {
    setError(form, err);
    return;
  }
  setError(form, "");

  const workerUrl = (form.getAttribute("data-bq-worker-url") || "").trim();
  if (!workerUrl) {
    setError(form, "Form isn't configured — please call us on 03331 126993.");
    return;
  }

  const payload = buildPayload(form);
  if (!payload.turnstile_token) {
    setError(
      form,
      "Please wait for the security check to finish, then submit again.",
    );
    return;
  }

  setSubmitting(form, true);
  const answer = await post(workerUrl, "/book", payload);
  if (answer.status !== 200) {
    // Turnstile tokens are single-use: a fresh one for the next try.
    resetTurnstile();
    setError(form, SEND_FAILED);
    setSubmitting(form, false);
    return;
  }
  window.location.href = nextPage(answer.data);
};

const buildSaveQuotePayload = (saveQuoteForm) => {
  const turnstileEl = saveQuoteForm.querySelector(
    "[name='cf-turnstile-response']",
  );
  return {
    turnstile_token: turnstileEl && turnstileEl.value ? turnstileEl.value : "",
    customer: {
      first_name: getPickerName(),
      email: fieldValue(saveQuoteForm, "email"),
    },
    event: {
      type: getPickerEventType(),
      date: getPickerEventDate(),
    },
    products: buildProducts(),
    totals: buildTotalsPayload(),
    coupon: couponPayload(),
  };
};

const getPickerName = () => {
  const input = document.querySelector("[data-bq-form] [name='first_name']");
  return input ? input.value.trim() : "";
};
const getPickerEventType = () => {
  const input = document.querySelector("[data-bq-form] [name='event_type']");
  return input ? input.value.trim() : "";
};
const getPickerEventDate = () => {
  const input = document.querySelector("[data-bq-form] [name='event_date']");
  return input ? input.value.trim() : "";
};

const setSaveQuoteMsg = (saveQuoteEl, message, kind) => {
  const msg = saveQuoteEl.querySelector("[data-bq-save-quote-msg]");
  if (!msg) return;
  msg.textContent = message || "";
  msg.hidden = !message;
  msg.classList.remove("is-ok", "is-error");
  if (kind) msg.classList.add(`is-${kind}`);
};

const setSaveQuoteSubmitting = (saveQuoteEl, isSubmitting) => {
  const btn = saveQuoteEl.querySelector("[data-bq-save-quote-submit]");
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.textContent = isSubmitting ? "Sending…" : "Email me this quote";
};

const submitSaveQuote = async (saveQuoteEl) => {
  const form = saveQuoteEl.querySelector("[data-bq-save-quote-form]");
  if (!form) return;

  if (SELECTION.size === 0) {
    setSaveQuoteMsg(saveQuoteEl, "Pick at least one product first.", "error");
    return;
  }

  const email = fieldValue(form, "email");
  if (!email || !EMAIL_RE.test(email)) {
    setSaveQuoteMsg(saveQuoteEl, "Please enter a valid email address.", "error");
    return;
  }

  const workerUrl = (
    saveQuoteEl.getAttribute("data-bq-worker-url") || ""
  ).trim();
  if (!workerUrl) {
    setSaveQuoteMsg(
      saveQuoteEl,
      "Save isn't configured — please call us on 03331 126993.",
      "error",
    );
    return;
  }

  const payload = buildSaveQuotePayload(form);
  if (!payload.turnstile_token) {
    setSaveQuoteMsg(
      saveQuoteEl,
      "Please wait for the security check to finish, then try again.",
      "error",
    );
    return;
  }

  setSaveQuoteMsg(saveQuoteEl, "");
  setSaveQuoteSubmitting(saveQuoteEl, true);

  let ok = false;
  try {
    const response = await fetch(`${workerUrl.replace(/\/$/, "")}/save-quote`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    ok = response.ok;
  } catch (_err) {
    ok = false;
  }

  setSaveQuoteSubmitting(saveQuoteEl, false);

  if (!ok) {
    resetSaveQuoteTurnstile(saveQuoteEl);
    setSaveQuoteMsg(
      saveQuoteEl,
      "Couldn't send right now. Please try again, or call us on 03331 126993.",
      "error",
    );
    return;
  }

  resetSaveQuoteTurnstile(saveQuoteEl);
  setSaveQuoteMsg(
    saveQuoteEl,
    `Sent — check ${email}. (It might take a moment, and may be in your spam folder.)`,
    "ok",
  );
};

const resetSaveQuoteTurnstile = (_saveQuoteEl) =>
  resetTurnstileWidget("bq-turnstile-save-quote");

// /coupon needs no Turnstile token (tokens are single-use, and the customer
// still needs theirs to submit); the Worker rate-limits it instead.
const checkCoupon = async (workerUrl, code) => {
  try {
    const response = await fetch(`${workerUrl.replace(/\/$/, "")}/coupon`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        code,
        tier: CURRENT_TIER,
        products: buildProducts(),
        venue_postcode: venuePostcodeValue(),
      }),
    });
    if (response.status === 429) {
      return {
        valid: false,
        error: "Too many tries — please wait a minute and try again.",
      };
    }
    return response.ok ? await response.json() : null;
  } catch (_err) {
    return null;
  }
};

const setCouponBusy = (couponEl, isBusy) => {
  const btn = couponEl.querySelector("[data-bq-coupon-apply]");
  if (!btn) return;
  btn.disabled = isBusy;
  btn.textContent = isBusy ? "Checking…" : "Apply";
};

const removeCoupon = (couponEl) => {
  COUPON = null;
  const input = couponEl.querySelector("[data-bq-coupon-input]");
  if (input) input.value = "";
  setCouponMsg(couponEl, "");
  renderSelection();
};

const applyCoupon = async (couponEl) => {
  const input = couponEl.querySelector("[data-bq-coupon-input]");
  const code = input ? input.value.trim() : "";
  if (!code) {
    setCouponMsg(couponEl, "Enter your code first.", "error");
    return;
  }
  const workerUrl = (couponEl.getAttribute("data-bq-worker-url") || "").trim();
  if (!workerUrl) {
    setCouponMsg(
      couponEl,
      "Codes can't be checked right now — add yours to the notes and we'll apply it.",
      "error",
    );
    return;
  }
  setCouponBusy(couponEl, true);
  const result = await checkCoupon(workerUrl, code);
  setCouponBusy(couponEl, false);
  if (!result || !result.valid) {
    setCouponMsg(
      couponEl,
      (result && result.error) ||
        "Couldn't check that code — please try again.",
      "error",
    );
    return;
  }
  COUPON = result.coupon;
  renderSelection();
};

const toggleCoupon = (couponEl) =>
  COUPON ? removeCoupon(couponEl) : applyCoupon(couponEl);

const handleClick = (event) => {
  const addBtn = event.target.closest("[data-bq-add]");
  if (addBtn) {
    const card = addBtn.closest("[data-bq-product]");
    if (card) {
      event.preventDefault();
      toggleProduct(card, addBtn);
    }
    return;
  }
  const incBtn = event.target.closest("[data-bq-qty-inc]");
  if (incBtn) {
    const li = incBtn.closest(".bq-selection__item");
    if (li) {
      event.preventDefault();
      adjustQuantity(li.dataset.slug, 1);
    }
    return;
  }
  const decBtn = event.target.closest("[data-bq-qty-dec]");
  if (decBtn) {
    const li = decBtn.closest(".bq-selection__item");
    if (li) {
      event.preventDefault();
      adjustQuantity(li.dataset.slug, -1);
    }
    return;
  }
  const removeBtn = event.target.closest("[data-bq-remove]");
  if (removeBtn) {
    const li = removeBtn.closest(".bq-selection__item");
    if (li) {
      event.preventDefault();
      removeFromSelection(li.dataset.slug);
    }
  }
};

const isPickerStepActive = () => {
  const picker = document.querySelector("[data-bq-step='picker']");
  return picker ? !picker.hidden : true;
};

const showStep = (name) => {
  for (const el of document.querySelectorAll("[data-bq-step]")) {
    el.hidden = el.getAttribute("data-bq-step") !== name;
  }
  // The "enter your postcode on the next page" note only makes sense on the
  // picker step — the form step IS the page with the postcode field.
  const estimateNote = document.querySelector("[data-bq-estimate-note]");
  if (estimateNote) estimateNote.hidden = name !== "picker";
  window.scrollTo({ top: 0, behavior: "smooth" });
  renderSelection();
  // Mount the lead-form Turnstile widget the first time the user reaches
  // the form step. Doing this on demand keeps Turnstile's fingerprint
  // probing out of the initial page-load console.
  if (name === "form") {
    const form = document.querySelector("[data-bq-form]");
    const sitekey = form
      ? form.getAttribute("data-bq-turnstile-key") || ""
      : "";
    renderTurnstile("bq-turnstile-lead", "lead", sitekey);
  }
};

const ROUTES_URL =
  "https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix";

let DELIVERY_REQUEST_TOKEN = 0;

const parsePrefixList = (raw) =>
  String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const getDeliveryConfig = (form) => ({
  apiKey: form.getAttribute("data-bq-routes-api-key") || "",
  origin: (form.getAttribute("data-bq-origin-postcode") || "").trim(),
  minCharge:
    Number.parseFloat(form.getAttribute("data-bq-delivery-min")) || 0,
  londonSupplement:
    Number.parseFloat(form.getAttribute("data-bq-delivery-london-supplement")) ||
    0,
  londonSupplementCorporate:
    Number.parseFloat(
      form.getAttribute("data-bq-delivery-london-supplement-corporate"),
    ) || 0,
  londonPrefixes: parsePrefixList(
    form.getAttribute("data-bq-delivery-london-prefixes"),
  ),
  ratePerMile:
    Number.parseFloat(form.getAttribute("data-bq-delivery-rate")) || 0,
});

const buildLondonRegex = (prefixes) => {
  if (!prefixes || prefixes.length === 0) return null;
  const sorted = [...prefixes].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`^(${escaped.join("|")})\\d`, "i");
};

const isInnerLondonPostcode = (postcode, prefixes) => {
  if (!postcode) return false;
  const re = buildLondonRegex(prefixes);
  if (!re) return false;
  return re.test(postcode.trim());
};

const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/;

// "kt113 qb" → "KT11 3QB". The inward code is always the last 3 characters,
// so a full postcode can be re-spaced reliably. Mis-spaced postcodes sent
// to the Routes API as typed got geocoded hundreds of miles away. Anything
// that isn't a full postcode is left as typed.
const tidyPostcode = (value) => {
  const compact = (value || "").replace(/\s+/g, "").toUpperCase();
  if (!UK_POSTCODE_RE.test(compact)) return (value || "").trim();
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
};

const fetchDelivery = async (form) => {
  const cfg = getDeliveryConfig(form);
  const venuePostcodeInput = form.querySelector("[name=venue_postcode]");
  const venue = venuePostcodeInput ? tidyPostcode(venuePostcodeInput.value) : "";
  // Show the customer (and the lead email) the tidied version.
  if (venuePostcodeInput && venue) venuePostcodeInput.value = venue;

  if (!cfg.apiKey || !cfg.origin || !cfg.ratePerMile || !venue) {
    DELIVERY_REQUEST_TOKEN++;
    DELIVERY.status = "idle";
    DELIVERY.postcode = "";
    renderSelection();
    return;
  }

  if (DELIVERY.status === "ok" && DELIVERY.postcode === venue) return;

  const myToken = ++DELIVERY_REQUEST_TOKEN;
  DELIVERY.status = "loading";
  DELIVERY.postcode = venue;
  renderSelection();

  try {
    const response = await fetch(ROUTES_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": cfg.apiKey,
        "X-Goog-FieldMask":
          "originIndex,destinationIndex,distanceMeters,duration,condition",
      },
      body: JSON.stringify({
        origins: [{ waypoint: { address: `${cfg.origin}, UK` } }],
        destinations: [{ waypoint: { address: `${venue}, UK` } }],
        travelMode: "DRIVE",
        routingPreference: "TRAFFIC_AWARE",
      }),
    });
    if (myToken !== DELIVERY_REQUEST_TOKEN) return;
    if (!response.ok) throw new Error(`Routes API ${response.status}`);
    const raw = await response.json();
    const elements = Array.isArray(raw) ? raw : [raw];
    const result = elements.find(
      (e) => e && e.condition === "ROUTE_EXISTS" && e.distanceMeters,
    );
    if (!result) throw new Error("No route");

    const miles = result.distanceMeters / METRES_PER_MILE;
    // Round the mileage charge up to the next whole pound so quotes never
    // show stray pennies (e.g. 17.3 mi × £1.25 = £21.63 → £22).
    const baseCharge = Math.max(
      cfg.minCharge,
      Math.ceil(miles * cfg.ratePerMile),
    );
    if (myToken !== DELIVERY_REQUEST_TOKEN) return;
    DELIVERY.status = "ok";
    DELIVERY.miles = miles;
    DELIVERY.baseChargeNet = baseCharge;
    DELIVERY.isLondon = isInnerLondonPostcode(venue, cfg.londonPrefixes);
    DELIVERY.londonSupplement = cfg.londonSupplement;
    DELIVERY.londonSupplementCorporate = cfg.londonSupplementCorporate;
  } catch (_err) {
    if (myToken !== DELIVERY_REQUEST_TOKEN) return;
    DELIVERY.status = "error";
  }
  renderSelection();
  if (DELIVERY.status === "ok") flashTotals();
};

const initFloatingCart = () => {
  const pill = document.querySelector("[data-bq-floating-cart]");
  const sidebar = document.querySelector("[data-bq-selection]");
  if (!pill || !sidebar) return;

  pill.addEventListener("click", () => {
    sidebar.scrollIntoView({ behavior: "smooth", block: "start" });
  });

  if (typeof IntersectionObserver === "undefined") return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        pill.classList.toggle("is-sidebar-visible", entry.isIntersecting);
      }
    },
    { threshold: 0.15 },
  );
  observer.observe(sidebar);
};

const init = () => {
  const form = document.querySelector("[data-bq-form]");
  if (!form) return;
  prefillFromUrl(form);
  setMinEventDate(form);
  document.addEventListener("click", handleClick);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    submitForm(form);
  });

  const continueBtn = document.querySelector("[data-bq-continue]");
  if (continueBtn) {
    continueBtn.addEventListener("click", () => showStep("form"));
  }
  const backBtn = document.querySelector("[data-bq-back]");
  if (backBtn) {
    backBtn.addEventListener("click", () => showStep("picker"));
  }

  const postcodeInput = form.querySelector("[name=venue_postcode]");
  if (postcodeInput) {
    postcodeInput.addEventListener("blur", () => fetchDelivery(form));
    // Venue-limited coupons switch on/off as the postcode changes.
    postcodeInput.addEventListener("change", () => {
      if (COUPON && COUPON.venue_postcodes) renderSelection();
    });
  }

  // Sync the body[data-tier] from the (possibly URL-prefilled) event-type
  // select so the right chip set is visible from the first paint. No
  // confirm needed — cart starts empty.
  const eventTypeSelect = form.querySelector("[name=event_type]");
  if (eventTypeSelect) {
    PREVIOUS_EVENT_TYPE_VALUE = eventTypeSelect.value;
    CURRENT_TIER = readPricingTierFromForm();
    setBodyTier(CURRENT_TIER);
    eventTypeSelect.addEventListener("change", () => refreshPricingTier());
  } else {
    setBodyTier(CURRENT_TIER);
  }

  const saveQuoteEl = document.querySelector("[data-bq-save-quote]");
  if (saveQuoteEl) {
    const saveQuoteForm = saveQuoteEl.querySelector("[data-bq-save-quote-form]");
    if (saveQuoteForm) {
      prefillFromUrl(saveQuoteForm);
      saveQuoteForm.addEventListener("submit", (event) => {
        event.preventDefault();
        submitSaveQuote(saveQuoteEl);
      });
    }
  }

  // The coupon box lives in the sidebar, outside any <form>, so Enter in
  // its input is wired up by hand.
  const couponEl = document.querySelector("[data-bq-coupon]");
  if (couponEl) {
    const applyBtn = couponEl.querySelector("[data-bq-coupon-apply]");
    if (applyBtn) {
      applyBtn.addEventListener("click", () => toggleCoupon(couponEl));
    }
    const couponInput = couponEl.querySelector("[data-bq-coupon-input]");
    if (couponInput) {
      couponInput.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        toggleCoupon(couponEl);
      });
    }
  }

  initFloatingCart();
  renderSelection();
  reapplyRestrictions();
};

onReady(init);
